import React from 'react'
import { BuildingOffice2Icon } from '@heroicons/react/24/outline'
import { Heading, Text, Input, Select } from '../../components/ui'
import { FieldRow, SectionCard } from '../../components/comercial/ClienteContaLayout'

const EMPTY = '—'

type GroupHealth = 'saudavel' | 'atencao' | 'risco' | 'critico'

type GroupConvenioRow = {
  id: string
  empresa: string
  cnpj: string
  apolice: string
  plano: string
  vidas: number
  valorMensal: number
  /** ISO yyyy-mm-dd; vazio = sem data */
  renovacao: string
  saude: GroupHealth | null
}

type SortBy = 'renovacao' | 'empresa' | 'vidas'

const HEALTH_LABEL: Record<GroupHealth, string> = {
  saudavel: 'Saudável',
  atencao: 'Atenção',
  risco: 'Risco',
  critico: 'Crítico',
}

/** Fonte da tabela — trocar por dados do service quando o vínculo de grupo existir. */
const GROUP_CONVENIOS: GroupConvenioRow[] = []

function matchesSearch(row: GroupConvenioRow, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const digits = q.replace(/\D/g, '')
  const haystack = `${row.empresa} ${row.cnpj} ${row.apolice}`.toLowerCase()
  if (haystack.includes(q)) return true
  if (digits.length >= 3) {
    const cnpjDigits = row.cnpj.replace(/\D/g, '')
    const apoliceDigits = row.apolice.replace(/\D/g, '')
    return cnpjDigits.includes(digits) || apoliceDigits.includes(digits)
  }
  return false
}

function compareRows(a: GroupConvenioRow, b: GroupConvenioRow, sortBy: SortBy): number {
  if (sortBy === 'empresa') {
    return a.empresa.localeCompare(b.empresa, 'pt-BR', { sensitivity: 'base' })
  }
  if (sortBy === 'vidas') {
    return b.vidas - a.vidas
  }
  // renovacao: datas mais próximas primeiro; sem data no fim
  const aDate = a.renovacao || '9999-12-31'
  const bDate = b.renovacao || '9999-12-31'
  return aDate.localeCompare(bDate)
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatRenovacao(value: string): string {
  if (!value) return EMPTY
  const [y, m, d] = value.split('-')
  if (!y || !m || !d) return value
  return `${d}/${m}/${y}`
}

function countByHealth(rows: GroupConvenioRow[]): Record<GroupHealth, number> {
  return {
    saudavel: rows.filter((r) => r.saude === 'saudavel').length,
    atencao: rows.filter((r) => r.saude === 'atencao').length,
    risco: rows.filter((r) => r.saude === 'risco').length,
    critico: rows.filter((r) => r.saude === 'critico').length,
  }
}

function KpiCard({
  title,
  value,
  hint,
}: {
  title: string
  value: string
  hint: string
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-3 py-2.5 min-w-0">
      <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {title}
      </Text>
      <Text size="sm" className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
        {value}
      </Text>
      <Text size="xs" color="muted" className="mt-1">
        {hint}
      </Text>
    </div>
  )
}

const TABLE_COLUMNS = [
  'Empresa | Unidade',
  'Apólice',
  'Plano contratado',
  'Vidas',
  'Valor mensal',
  'Renovação',
  'Saúde',
  '',
] as const

const ClienteContaGrupoTab: React.FC = () => {
  const [search, setSearch] = React.useState('')
  const [healthFilter, setHealthFilter] = React.useState<'' | GroupHealth>('')
  const [sortBy, setSortBy] = React.useState<SortBy>('renovacao')

  const hasSourceData = GROUP_CONVENIOS.length > 0

  const visibleRows = React.useMemo(() => {
    const filtered = GROUP_CONVENIOS.filter((row) => {
      if (healthFilter && row.saude !== healthFilter) return false
      return matchesSearch(row, search)
    })
    return [...filtered].sort((a, b) => compareRows(a, b, sortBy))
  }, [search, healthFilter, sortBy])

  const healthCounts = React.useMemo(() => countByHealth(GROUP_CONVENIOS), [])

  const emptyMessage = !hasSourceData
    ? 'Vínculo com grupo em breve. Quando houver grupo, os convênios aparecerão aqui.'
    : 'Nenhum convênio encontrado com os filtros atuais.'

  return (
    <div className="space-y-3">
      {/* Header do grupo */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3">
        <Text size="sm" color="muted">
          Painel de indicadores de cobertura, convênios e prioridades do grupo
        </Text>
        <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#008B95]/10 dark:bg-teal-900/40">
              <BuildingOffice2Icon className="h-5 w-5 text-[#008B95] dark:text-teal-400" />
            </div>
            <div className="min-w-0">
              <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
                {EMPTY}
              </Heading>
              <Text size="xs" className="mt-0.5 uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Grupo econômico
              </Text>
              <Text size="sm" color="muted" className="mt-1">
                CNPJ matriz: {EMPTY} · {EMPTY} empresas vinculadas · Atualizado em: {EMPTY}
              </Text>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-gray-600" />
            <Text size="sm" className="text-gray-700 dark:text-gray-300">
              Saúde geral: {EMPTY}
            </Text>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard title="Convênios ativos" value={EMPTY} hint="Exibir convênios em atraso" />
        <KpiCard title="Total de vidas" value={EMPTY} hint="Variação nos últimos 30 dias" />
        <KpiCard title="Faturamento mensal" value={EMPTY} hint="Soma das faturas vigentes" />
        <KpiCard title="Próxima renovação" value={EMPTY} hint="Convênios em atenção" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_18rem] gap-3 items-start">
        {/* Convênios do grupo */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow min-w-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Heading as="h3" size="lg" className="text-gray-900 dark:text-white">
                Convênios do grupo
              </Heading>
              <Text size="sm" color="muted">
                {visibleRows.length} resultado{visibleRows.length === 1 ? '' : 's'}
              </Text>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
              <Input
                placeholder="Buscar por empresa, CNPJ ou apólice"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={!hasSourceData}
                className="w-full"
              />
              <Select
                value={healthFilter}
                onChange={(e) => setHealthFilter(e.target.value as '' | GroupHealth)}
                disabled={!hasSourceData}
                className="w-full md:w-40"
              >
                <option value="">Saúde: Todas</option>
                <option value="saudavel">Saudável</option>
                <option value="atencao">Atenção</option>
                <option value="risco">Risco</option>
                <option value="critico">Crítico</option>
              </Select>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                disabled={!hasSourceData}
                className="w-full md:w-44"
              >
                <option value="renovacao">Ordenar: Renovação</option>
                <option value="empresa">Ordenar: Empresa</option>
                <option value="vidas">Ordenar: Vidas</option>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {TABLE_COLUMNS.map((col) => (
                    <th
                      key={col || 'acao'}
                      className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLUMNS.length} className="px-4 py-8 text-center">
                      <Text size="sm" color="muted">
                        {emptyMessage}
                      </Text>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row) => (
                    <tr key={row.id} className="bg-white dark:bg-gray-800">
                      <td className="px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100">
                        <div className="font-medium">{row.empresa || EMPTY}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {row.cnpj || EMPTY}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
                        {row.apolice || EMPTY}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
                        {row.plano || EMPTY}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
                        {row.vidas.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
                        {formatCurrency(row.valorMensal)}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
                        {formatRenovacao(row.renovacao)}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">
                        {row.saude ? HEALTH_LABEL[row.saude] : EMPTY}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-primary-600 dark:text-primary-400">
                        {/* Ação por linha quando houver rota/conta vinculada */}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <Heading as="h4" size="lg" className="text-gray-900 dark:text-white mb-1">
              Resumo consolidado
            </Heading>
            <Text size="sm" color="muted">
              {healthCounts.saudavel} saudáveis · {healthCounts.atencao} em atenção ·{' '}
              {healthCounts.risco + healthCounts.critico} em risco
            </Text>
            <Text size="xs" color="muted" className="mt-1">
              A saúde geral considera o faturamento entre os convênios e lhes atribui a
              prioridade de atendimento do grupo.
            </Text>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-3 min-w-0">
          <SectionCard title="Dados do grupo">
            <FieldRow label="Responsável principal" value={EMPTY} />
            <FieldRow label="Consultor PASI" value={EMPTY} />
            <FieldRow label="Gerente de conta" value={EMPTY} />
            <FieldRow label="Telefone" value={EMPTY} />
            <FieldRow label="E-mail" value={EMPTY} />
          </SectionCard>

          <SectionCard title="Responsáveis internos">
            <FieldRow label="Consultor CORE responsável" value={EMPTY} />
            <FieldRow label="Comercial" value={EMPTY} />
            <FieldRow label="Implantador" value={EMPTY} />
          </SectionCard>

          <SectionCard title="Próxima ação">
            <Text size="xs" className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Prioridade do grupo
            </Text>
            <Text size="sm" className="mt-1 text-gray-900 dark:text-gray-100">
              {EMPTY}
            </Text>
            <FieldRow label="Responsável" value={EMPTY} />
            <Text size="xs" color="muted" className="mt-2">
              A ação pode ser reagendada no Dashboard de Atendimento caso a data tenha passado.
            </Text>
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

export default ClienteContaGrupoTab
